import { captureUnexpectedAppException } from "~/lib/sentry";
import { clearPublicBrowseServiceWorkerCache } from "~/lib/public-browse-service-worker-client";

const STALE_BUILD_RELOAD_SESSION_KEY = "glider:webapp-v2:stale-build-reload-at";
const STALE_BUILD_RELOAD_COOLDOWN_MS = 30 * 1000;
const DEPRECATED_SERVICE_WORKER_CACHE_PREFIX = "glider-webapp-v2";
const DEPRECATED_SERVICE_WORKER_SCRIPT_PATH = "/sw.js";

interface VitePreloadErrorEvent extends Event {
  readonly payload?: unknown;
}

let handlerInstalled = false;
let reloadPage = defaultReloadPage;
let deprecatedServiceWorkerCleanupStarted = false;

function defaultReloadPage(): void {
  window.location.reload();
}

function getSessionStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readLastReloadAttemptMs(storage: Storage | null): number | null {
  const rawValue = storage?.getItem(STALE_BUILD_RELOAD_SESSION_KEY);
  if (!rawValue) {
    return null;
  }

  const parsedValue = Number.parseInt(rawValue, 10);
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function getDeprecatedServiceWorkerContainer(): ServiceWorkerContainer | null {
  if (
    typeof window === "undefined" ||
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator)
  ) {
    return null;
  }

  return navigator.serviceWorker;
}

function getRegistrationScriptUrl(
  registration: ServiceWorkerRegistration,
): string | null {
  return (
    registration.active?.scriptURL ??
    registration.waiting?.scriptURL ??
    registration.installing?.scriptURL ??
    null
  );
}

function isDeprecatedWebappV2ServiceWorkerRegistration(
  registration: ServiceWorkerRegistration,
): boolean {
  const scriptUrl = getRegistrationScriptUrl(registration);

  if (!scriptUrl) {
    return false;
  }

  try {
    const parsedUrl = new URL(scriptUrl);
    return (
      parsedUrl.origin === window.location.origin &&
      parsedUrl.pathname === DEPRECATED_SERVICE_WORKER_SCRIPT_PATH
    );
  } catch {
    return scriptUrl.endsWith(DEPRECATED_SERVICE_WORKER_SCRIPT_PATH);
  }
}

async function deleteDeprecatedWebappV2ServiceWorkerCaches(): Promise<void> {
  if (typeof caches === "undefined") {
    return;
  }

  const cacheNames = await caches.keys();
  await Promise.all(
    cacheNames.flatMap((cacheName) =>
      cacheName.startsWith(DEPRECATED_SERVICE_WORKER_CACHE_PREFIX)
        ? [caches.delete(cacheName)]
        : [],
    ),
  );
}

export async function cleanupDeprecatedWebappV2ServiceWorker(): Promise<void> {
  const serviceWorker = getDeprecatedServiceWorkerContainer();

  if (serviceWorker?.getRegistrations) {
    const registrations = await serviceWorker.getRegistrations();
    await Promise.all(
      registrations.flatMap((registration) =>
        isDeprecatedWebappV2ServiceWorkerRegistration(registration)
          ? [registration.unregister()]
          : [],
      ),
    );
  }

  await deleteDeprecatedWebappV2ServiceWorkerCaches();
}

function startDeprecatedServiceWorkerCleanup(): void {
  if (deprecatedServiceWorkerCleanupStarted) {
    return;
  }

  deprecatedServiceWorkerCleanupStarted = true;
  void cleanupDeprecatedWebappV2ServiceWorker().catch((error: unknown) => {
    captureUnexpectedAppException(error, {
      name: "webapp_v2_deprecated_service_worker_cleanup",
    });
  });
}

function handleVitePreloadError(event: Event): void {
  const storage = getSessionStorage();
  const now = Date.now();
  const lastReloadAttemptMs = readLastReloadAttemptMs(storage);
  const payload = (event as VitePreloadErrorEvent).payload;

  captureUnexpectedAppException(payload ?? event, {
    name: "webapp_v2_vite_preload_error",
  });

  if (
    lastReloadAttemptMs !== null &&
    now - lastReloadAttemptMs < STALE_BUILD_RELOAD_COOLDOWN_MS
  ) {
    return;
  }

  event.preventDefault();
  storage?.setItem(STALE_BUILD_RELOAD_SESSION_KEY, String(now));
  void clearPublicBrowseServiceWorkerCache().then(() => reloadPage());
}

export function installWebappV2ReleaseRecoveryHandlers(): void {
  if (typeof window === "undefined" || handlerInstalled) {
    return;
  }

  handlerInstalled = true;
  startDeprecatedServiceWorkerCleanup();
  window.addEventListener("vite:preloadError", handleVitePreloadError);
}

export function setWebappV2ReleaseRecoveryReloadForTests(
  reload: () => void,
): () => void {
  const previousReload = reloadPage;
  reloadPage = reload;

  return () => {
    reloadPage = previousReload;
  };
}

export function resetWebappV2ReleaseRecoveryForTests(): void {
  if (typeof window !== "undefined" && handlerInstalled) {
    window.removeEventListener("vite:preloadError", handleVitePreloadError);
  }

  handlerInstalled = false;
  deprecatedServiceWorkerCleanupStarted = false;
  reloadPage = defaultReloadPage;
}
